/**
 * PlaceCoreNearbyList — "I närheten, om du vill fortsätta"
 * Max 3 places, vertical list, no grid, no badges, no metrics.
 * If no nearby places: only show explore link for landskap.
 */
import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import type { NearbyPlaceItem } from "@/components/place/types";

interface Props {
  places: NearbyPlaceItem[];
  placeName: string;
  currentLandskap?: string;
}

export function PlaceCoreNearbyList({ places, placeName, currentLandskap }: Props) {
  const display = places.slice(0, 3);
  const hasPlaces = display.length > 0;

  // If no nearby places and no landskap, render nothing
  if (!hasPlaces && !currentLandskap) return null;

  return (
    <section>
      {hasPlaces ? (
        <>
          <h2 className="font-display text-lg text-foreground mb-4">
            I närheten, om du vill fortsätta
          </h2>

          <div className="space-y-0.5">
            {display.map((p) => (
              <Link
                key={p.id}
                to={`/place/${p.slug}`}
                className="group flex items-center justify-between py-3 px-3 -mx-3 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-primary/60" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate group-hover:text-primary transition-colors text-sm">
                      {p.name}
                    </p>
                    {(p.city || p.region) && (
                      <p className="text-xs text-muted-foreground truncate">
                        {p.city || p.region}
                      </p>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </>
      ) : null}

      {currentLandskap && (
        <div className={hasPlaces ? "mt-4 pt-3 border-t border-border/30" : ""}>
          <Link
            to={`/explore?region=${currentLandskap.toLowerCase()}`}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {hasPlaces
              ? `Visa fler i ${currentLandskap} →`
              : `Utforska fler platser i ${currentLandskap} →`}
          </Link>
        </div>
      )}
    </section>
  );
}
